/**
 * @module migrations
 * @package @geenius/adapters/neon
 * @description Declares the idempotent Neon/Postgres schema contract for the
 * generic adapter-record table used by conformance and local backend swaps.
 */

import { NeonBindingError } from "./errors";
import type { NeonMigration, NeonSqlClient, NeonSqlExecutor } from "./types";

/**
 * Table names owned by the Neon adapters backend contract. Consumers can use
 * this list to verify their schema before enabling the conformance adapter.
 */
export const neonAdapterTables = ["geenius_adapter_records"] as const;

/**
 * Idempotent SQL migration for the generic adapter records table used by the
 * Neon conformance adapter. The statement avoids provider SDK imports and can
 * run through any Postgres-compatible executor.
 */
export const NEON_MIGRATION_0001_ADAPTERS_CORE = `CREATE TABLE IF NOT EXISTS geenius_adapter_records (
  collection text NOT NULL,
  id text NOT NULL,
  payload jsonb NOT NULL,
  created_at timestamptz NOT NULL DEFAULT now(),
  updated_at timestamptz NOT NULL DEFAULT now(),
  PRIMARY KEY (collection, id)
);

CREATE INDEX IF NOT EXISTS geenius_adapter_records_by_collection
  ON geenius_adapter_records(collection);

CREATE INDEX IF NOT EXISTS geenius_adapter_records_payload_gin
  ON geenius_adapter_records USING gin (payload);

CREATE OR REPLACE FUNCTION geenius_adapter_records_set_updated_at()
RETURNS trigger AS $$
BEGIN
  NEW.updated_at = now();
  RETURN NEW;
END;
$$ LANGUAGE plpgsql;

DROP TRIGGER IF EXISTS geenius_adapter_records_set_updated_at
  ON geenius_adapter_records;

CREATE TRIGGER geenius_adapter_records_set_updated_at
  BEFORE UPDATE ON geenius_adapter_records
  FOR EACH ROW
  EXECUTE FUNCTION geenius_adapter_records_set_updated_at();`;

/**
 * Ordered Neon migrations shipped by this package. Apply them in sequence
 * before using the generic adapter-record table in tests or local bindings.
 */
export const neonMigrations = Object.freeze([
  Object.freeze({
    id: "0001_adapters_core",
    sql: NEON_MIGRATION_0001_ADAPTERS_CORE,
  }),
]) satisfies readonly NeonMigration[];

function isSqlExecutor(value: unknown): value is NeonSqlExecutor {
  return typeof value === "function";
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function toSqlExecutor(client: NeonSqlClient): NeonSqlExecutor {
  if (isSqlExecutor(client)) {
    return client;
  }

  if (isRecord(client) && typeof client.query === "function") {
    return (text, params) => client.query(text, params);
  }

  throw new NeonBindingError(
    "applyNeonMigrations expected a SQL executor or object with query().",
    "INVALID_NEON_OPTIONS",
  );
}

function assertMigrationList(
  migrations: unknown,
): asserts migrations is readonly NeonMigration[] {
  if (!Array.isArray(migrations)) {
    throw new NeonBindingError(
      "applyNeonMigrations expected migrations to be an array.",
      "INVALID_NEON_OPTIONS",
    );
  }
}

function assertMigration(
  migration: unknown,
): asserts migration is NeonMigration {
  if (
    !isRecord(migration) ||
    typeof migration.id !== "string" ||
    migration.id.length === 0 ||
    typeof migration.sql !== "string" ||
    migration.sql.trim().length === 0
  ) {
    throw new NeonBindingError(
      "applyNeonMigrations expected each migration to have a non-empty id and SQL body.",
      "INVALID_NEON_OPTIONS",
    );
  }
}

/**
 * Applies the ordered Neon migrations through a function or query-object SQL
 * executor and returns the migration ids that completed.
 *
 * @param client - SQL executor function or object exposing `query(sql)`.
 * @param migrations - Ordered migration list; defaults to this package's Neon migrations.
 * @returns Migration ids applied in order.
 * @throws {NeonBindingError} When the client shape is invalid or a migration fails.
 */
export async function applyNeonMigrations(
  client: NeonSqlClient,
  migrations: readonly NeonMigration[] = neonMigrations,
): Promise<string[]> {
  const query = toSqlExecutor(client);
  assertMigrationList(migrations);
  const applied: string[] = [];

  for (const migration of migrations) {
    assertMigration(migration);
    try {
      await query(migration.sql);
    } catch (error) {
      throw new NeonBindingError(
        `Failed to apply Neon migration: ${migration.id}`,
        "NEON_MIGRATION_FAILED",
        error,
      );
    }
    applied.push(migration.id);
  }

  return applied;
}
